// Minimap markers for champions: an owner-coloured star on every visible
// champion, plus a pulsing one on the city running the local summon.
// Empty (and referentially stable) while legends.enabled is off.

import type { GameSnapshot } from '@aod/protocol';
import type { MinimapMarker } from '@aod/ui';
import { useMemo } from 'react';
import { championMarkers } from './championRules.js';

const NONE: readonly MinimapMarker[] = [];
const FALLBACK_COLOR = '#f5c542';

/** Minimap glyph per kind (colour stays the owner's): star, diamond, four-point star. */
const KIND_GLYPH: Readonly<Record<string, string>> = { dragon: '★', golem: '◆', lich: '✦' };

export function toMinimapMarkers(
  snapshot: GameSnapshot | null,
  localPlayerIndex: number,
  playerColors: ReadonlyMap<number, string> | undefined,
): readonly MinimapMarker[] {
  const markers = championMarkers(snapshot, localPlayerIndex);
  if (markers.length === 0) return NONE;
  return markers.map((m) => ({
    q: m.q,
    r: m.r,
    color: playerColors?.get(m.ownerId) ?? FALLBACK_COLOR,
    pulsing: m.summoning,
    glyph: KIND_GLYPH[m.kind] ?? '★',
    label: m.summoning ? `Champion summoning (${m.kind})` : `Champion (${m.kind})`,
  }));
}

export function useChampionMarkers(
  snapshot: GameSnapshot | null,
  localPlayerIndex: number,
  playerColors: ReadonlyMap<number, string> | undefined,
): readonly MinimapMarker[] {
  return useMemo(
    () => toMinimapMarkers(snapshot, localPlayerIndex, playerColors),
    [snapshot, localPlayerIndex, playerColors],
  );
}
